import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicStore, createMpPreference, createCashPayment } from '../services/api';

/* ─── helpers ─────────────────────────────────────────────────────────────── */

function hexToRgb(hex = '#7c3aed') {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return r
    ? `${parseInt(r[1], 16)},${parseInt(r[2], 16)},${parseInt(r[3], 16)}`
    : '124,58,237';
}

function currencySymbol(code) {
  if (code === 'ARS') return '$';
  if (code === 'USD') return 'US$';
  if (code === 'EUR') return '€';
  return `${code} `;
}

/* ─── Cash payment modal ───────────────────────────────────────────────────── */

function CashModal({ product, config, clientId, onClose }) {
  const [cashMethod, setCashMethod] = useState('rapipago');
  const [email, setEmail]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [ticketUrl, setTicketUrl]   = useState('');

  const rgb = hexToRgb(config.color);
  const sym = currencySymbol(config.moneda);

  const handleSubmit = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Ingresá un email válido');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await createCashPayment(clientId, {
        payment_method_id: cashMethod,
        amount:            product.precio,
        description:       product.nombre,
        payer_email:       email,
      });
      if (result.status === 'pending') {
        setTicketUrl(result.ticket_url || '');
      } else {
        setError(`Estado inesperado: ${result.status}`);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const overlayStyle = {
    position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000,
    display:'flex', alignItems:'center', justifyContent:'center', padding:16,
  };
  const modalStyle = {
    background:'#fff', borderRadius:18, padding:28, width:'100%', maxWidth:420,
    boxShadow:'0 24px 64px rgba(0,0,0,0.22)', fontFamily:"'DM Sans',system-ui,sans-serif",
    position:'relative',
  };
  const btnBase = {
    padding:'11px 0', borderRadius:10, border:'none', cursor:'pointer',
    fontWeight:700, fontSize:14, fontFamily:'inherit', transition:'all 0.15s',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        {/* close */}
        <button
          onClick={onClose}
          style={{ position:'absolute', top:14, right:16, background:'none', border:'none',
            fontSize:20, cursor:'pointer', color:'#aaa', lineHeight:1 }}
        >✕</button>

        {ticketUrl ? (
          /* ── Ticket generado ── */
          <>
            <div style={{ textAlign:'center', marginBottom:20 }}>
              <div style={{ fontSize:48, marginBottom:8 }}>🎟️</div>
              <h3 style={{ color:'#1a1a2e', fontSize:18, fontWeight:700, margin:'0 0 6px' }}>
                ¡Ticket generado!
              </h3>
              <p style={{ color:'#666', fontSize:14, margin:0 }}>
                Pagá en {cashMethod === 'rapipago' ? 'Rapipago' : 'Pago Fácil'} con el siguiente ticket.
              </p>
            </div>
            <a
              href={ticketUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                display:'block', textAlign:'center', padding:'13px 0',
                background:`rgb(${rgb})`, color:'#fff', borderRadius:11,
                fontWeight:700, fontSize:15, textDecoration:'none',
                marginBottom:10,
              }}
            >
              Ver ticket de pago
            </a>
            <button
              onClick={onClose}
              style={{ ...btnBase, width:'100%', background:'#f3f4f6', color:'#555' }}
            >
              Cerrar
            </button>
          </>
        ) : (
          /* ── Formulario ── */
          <>
            {/* Producto */}
            <div style={{ marginBottom:20, paddingBottom:16, borderBottom:'1px solid #f0f0f0' }}>
              <p style={{ color:'#888', fontSize:12, margin:'0 0 2px' }}>Pagando</p>
              <h3 style={{ color:'#1a1a2e', fontSize:16, fontWeight:700, margin:'0 0 2px' }}>
                {product.nombre}
              </h3>
              <span style={{ color:`rgb(${rgb})`, fontSize:22, fontWeight:800 }}>
                {sym}{Number(product.precio).toLocaleString('es-AR')}
              </span>
            </div>

            {/* Método */}
            <p style={{ color:'#555', fontSize:13, fontWeight:600, margin:'0 0 8px' }}>
              Elegí dónde pagás:
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:18 }}>
              {[
                { id:'rapipago',  label:'Rapipago',   emoji:'🏪' },
                { id:'pagofacil', label:'Pago Fácil', emoji:'🏬' },
              ].map(m => (
                <button
                  key={m.id}
                  onClick={() => setCashMethod(m.id)}
                  style={{
                    ...btnBase,
                    border: cashMethod === m.id
                      ? `2px solid rgb(${rgb})`
                      : '2px solid #e5e7eb',
                    background: cashMethod === m.id ? `rgba(${rgb},0.06)` : '#fafafa',
                    color: cashMethod === m.id ? `rgb(${rgb})` : '#555',
                    padding:'13px 0',
                  }}
                >
                  <span style={{ display:'block', fontSize:22, marginBottom:4 }}>{m.emoji}</span>
                  {m.label}
                </button>
              ))}
            </div>

            {/* Email */}
            <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#555', marginBottom:6 }}>
              Tu email (para el ticket)
            </label>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              placeholder="ejemplo@correo.com"
              style={{
                width:'100%', padding:'11px 14px', borderRadius:10, marginBottom:14,
                border: error ? '1.5px solid #ef4444' : '1.5px solid #e5e7eb',
                fontSize:14, outline:'none', fontFamily:'inherit', boxSizing:'border-box',
              }}
              onFocus={e => { e.target.style.borderColor=`rgb(${rgb})`; }}
              onBlur={e => { e.target.style.borderColor= error ? '#ef4444' : '#e5e7eb'; }}
            />

            {error && (
              <p style={{ color:'#ef4444', fontSize:13, margin:'-8px 0 12px', fontWeight:500 }}>
                {error}
              </p>
            )}

            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                ...btnBase, width:'100%',
                background: loading ? '#d1d5db' : `rgb(${rgb})`,
                color:'#fff', fontSize:15,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Generando ticket...' : 'Generar ticket de pago'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Product card ─────────────────────────────────────────────────────────── */

function ProductCard({ product, config, onBuyMP, mpBuying, onBuyCash }) {
  const [hover, setHover] = useState(false);
  const rgb = hexToRgb(config.color);
  const sym = currencySymbol(config.moneda);

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(
      `Hola! Me interesa *${product.nombre}* (${sym}${Number(product.precio).toLocaleString('es-AR')}). ¿Está disponible?`
    );
    window.open(`https://wa.me/${config.whatsapp}?text=${msg}`, '_blank');
  };

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background:'#fff',borderRadius:18,overflow:'hidden',
        boxShadow: hover
          ? `0 18px 48px rgba(${rgb},0.22),0 4px 16px rgba(0,0,0,0.1)`
          : '0 2px 12px rgba(0,0,0,0.07)',
        transform: hover ? 'translateY(-5px) scale(1.01)' : 'none',
        transition:'all 0.28s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      {/* Image */}
      <div style={{ position:'relative',paddingBottom:'68%',overflow:'hidden',background:'#f5f5f5' }}>
        {product.imagen ? (
          <img
            src={product.imagen}
            alt={product.nombre}
            style={{
              position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',
              transition:'transform 0.35s ease',
              transform: hover ? 'scale(1.06)' : 'scale(1)',
            }}
            onError={e => { e.target.parentNode.style.background='#eee'; e.target.style.display='none'; }}
          />
        ) : (
          <div style={{ position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:52,color:'#ddd' }}>
            👟
          </div>
        )}
        {product.destacado && (
          <div style={{
            position:'absolute',top:10,left:10,
            background:`rgba(${rgb},0.92)`,backdropFilter:'blur(6px)',
            borderRadius:7,padding:'3px 10px',fontSize:11,fontWeight:700,color:'#fff',
          }}>
            ⭐ DESTACADO
          </div>
        )}
        {product.categoria && (
          <div style={{
            position:'absolute',top:10,right:10,
            background:'rgba(255,255,255,0.9)',backdropFilter:'blur(6px)',
            borderRadius:7,padding:'3px 10px',fontSize:11,fontWeight:600,color:'#555',
          }}>
            {product.categoria}
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding:'16px 18px 18px' }}>
        <h3 style={{ color:'#1a1a2e',fontSize:15,fontWeight:700,margin:'0 0 5px',lineHeight:1.3 }}>
          {product.nombre}
        </h3>
        {product.descripcion && (
          <p style={{
            color:'#888',fontSize:13,lineHeight:1.5,margin:'0 0 10px',
            display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',
          }}>
            {product.descripcion}
          </p>
        )}

        <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10,flexWrap:'wrap' }}>
          <div>
            <span style={{ color:`rgb(${rgb})`,fontSize:22,fontWeight:800,fontFamily:'system-ui,sans-serif' }}>
              {sym}{Number(product.precio).toLocaleString('es-AR')}
            </span>
            {/* Stock indicator — solo si está configurado */}
            {product.stock !== undefined && product.stock !== null && (
              <div style={{ marginTop:6, fontSize:13, fontWeight:600 }}>
                {product.stock > 0 ? (
                  <span style={{ color:'#16a34a' }}>✓ {product.stock} en stock</span>
                ) : (
                  <span style={{ color:'#dc2626', fontWeight:700 }}>✕ Sin stock</span>
                )}
              </div>
            )}
          </div>

          <div style={{ display:'flex',gap:7,flexWrap:'wrap' }}>
            {/* WhatsApp button */}
            {config.whatsapp && (
              <button
                onClick={handleWhatsApp}
                style={{
                  padding:'9px 13px',borderRadius:9,border:'none',cursor:'pointer',
                  background:'#25D366',color:'#fff',fontWeight:700,fontSize:13,
                  display:'flex',alignItems:'center',gap:6,
                  transition:'transform 0.18s,box-shadow 0.18s',fontFamily:'inherit',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform='scale(1.06)'; e.currentTarget.style.boxShadow='0 4px 14px rgba(37,211,102,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.553 4.116 1.524 5.849L.057 23.569a.5.5 0 00.624.624l5.7-1.47A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.96 0-3.79-.574-5.33-1.562l-.38-.244-3.942 1.016 1.042-3.805-.263-.393A9.94 9.94 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
                </svg>
                WhatsApp
              </button>
            )}

            {/* MercadoPago — tarjeta */}
            {config.mp_enabled && (
              <button
                onClick={() => onBuyMP(product)}
                disabled={mpBuying === product.id || (product.stock !== undefined && product.stock !== null && product.stock <= 0)}
                style={{
                  padding:'9px 13px',borderRadius:9,border:'none',cursor: (product.stock !== undefined && product.stock !== null && product.stock <= 0) ? 'not-allowed' : 'pointer',
                  background: (product.stock !== undefined && product.stock !== null && product.stock <= 0) ? '#d1d5db' : `rgb(${rgb})`,color:'#fff',fontWeight:700,fontSize:13,
                  transition:'transform 0.18s,opacity 0.18s',fontFamily:'inherit',
                  opacity: mpBuying === product.id || (product.stock !== undefined && product.stock !== null && product.stock <= 0) ? 0.65 : 1,
                }}
                onMouseEnter={e => { if (!mpBuying && !(product.stock !== undefined && product.stock !== null && product.stock <= 0)) e.currentTarget.style.transform='scale(1.06)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform=''; }}
              >
                {mpBuying === product.id ? '...' : '💳 Tarjeta'}
              </button>
            )}

            {/* MercadoPago — efectivo */}
            {config.mp_enabled && (
              <button
                onClick={() => onBuyCash(product)}
                disabled={product.stock !== undefined && product.stock !== null && product.stock <= 0}
                style={{
                  padding:'9px 13px',borderRadius:9,fontWeight:700,fontSize:13,
                  border: (product.stock !== undefined && product.stock !== null && product.stock <= 0) ? `1.5px solid #d1d5db` : `1.5px solid rgb(${rgb})`,cursor: (product.stock !== undefined && product.stock !== null && product.stock <= 0) ? 'not-allowed' : 'pointer',
                  background:'#fff',color: (product.stock !== undefined && product.stock !== null && product.stock <= 0) ? '#d1d5db' : `rgb(${rgb})`,fontFamily:'inherit',
                  transition:'transform 0.18s,background 0.18s',
                  opacity: (product.stock !== undefined && product.stock !== null && product.stock <= 0) ? 0.5 : 1,
                }}
                onMouseEnter={e => { if (!(product.stock !== undefined && product.stock !== null && product.stock <= 0)) { e.currentTarget.style.background=`rgba(${rgb},0.07)`; e.currentTarget.style.transform='scale(1.06)'; } }}
                onMouseLeave={e => { e.currentTarget.style.background='#fff'; e.currentTarget.style.transform=''; }}
              >
                💵 Efectivo
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main public store ────────────────────────────────────────────────────── */

export default function PublicStore() {
  // Route: /tienda/:clientId  ← uses PHP clientId (UUID)
  const { clientId } = useParams();

  const [store, setStore]       = useState(null);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [category, setCategory] = useState('');
  const [mpBuying, setMpBuying]       = useState(null);
  const [mpMsg, setMpMsg]             = useState('');
  const [cashProduct, setCashProduct] = useState(null);
  const [cardPrompt, setCardPrompt]   = useState(null);  // { product, email }
  const [cardEmail, setCardEmail]     = useState('');
  const [cardEmailErr, setCardEmailErr] = useState('');

  useEffect(() => {
    // GET api/store/:clientId  (public, no auth)
    // PHP returns: { config: { nombre, descripcion, color, moneda, whatsapp, mp_enabled }, productos: [...] }
    getPublicStore(clientId)
      .then(setStore)
      .catch(err => setError(err.message));

    // MP status from query params
    const params = new URLSearchParams(window.location.search);
    const mp = params.get('mp');
    if (mp === 'success') setMpMsg('✅ ¡Pago exitoso! Gracias por tu compra.');
    if (mp === 'failure') setMpMsg('❌ El pago no pudo completarse. Intentá de nuevo.');
    if (mp === 'pending') setMpMsg('⏳ Tu pago está pendiente de acreditación.');
  }, [clientId]);

  // Paso 1: abrir mini-modal para pedir email
  const handleBuyMP = (product) => {
    setCardEmail('');
    setCardEmailErr('');
    setCardPrompt(product);
  };

  // Paso 2: con email confirmado, crear preferencia y redirigir
  const handleCardConfirm = async () => {
    if (!cardEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cardEmail)) {
      setCardEmailErr('Ingresá un email válido');
      return;
    }
    const product = cardPrompt;
    setCardPrompt(null);
    setMpBuying(product.id);
    try {
      const data = await createMpPreference(clientId, {
        id:          product.id,
        nombre:      product.nombre,
        precio:      product.precio,
        descripcion: product.descripcion || '',
        imagen:      product.imagen || '',
        stock:       product.stock || 0,
        cantidad:    1,
      }, cardEmail);
      window.location.href = data.checkout_url || data.init_point;
    } catch (err) {
      alert('Error al procesar el pago: ' + err.message);
      setMpBuying(null);
    }
  };

  // Derived data
  const config   = store?.config   || {};
  const products = store?.productos || [];
  const rgb      = hexToRgb(config.color);

  const categories = useMemo(
    () => [...new Set(products.map(p => p.categoria).filter(Boolean))],
    [products]
  );

  const filtered = useMemo(() => {
    let list = products;
    if (category) list = list.filter(p => p.categoria === category);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.nombre?.toLowerCase().includes(q) ||
        p.descripcion?.toLowerCase().includes(q) ||
        p.categoria?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [products, category, search]);

  const featured      = products.filter(p => p.destacado);
  const nonFeatured   = filtered.filter(p => !p.destacado || search || category);

  /* ── error state ── */
  if (error) return (
    <div style={{ minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f9f9f9',fontFamily:'system-ui,sans-serif',padding:20 }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:64,marginBottom:14 }}>🔒</div>
        <h2 style={{ color:'#1a1a2e',marginBottom:8 }}>Tienda no disponible</h2>
        <p style={{ color:'#888' }}>{error}</p>
      </div>
    </div>
  );

  /* ── loading ── */
  if (!store) return (
    <div style={{ minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f9f9f9' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:44,height:44,border:`3px solid rgba(${rgb},0.2)`,borderTopColor:`rgb(${rgb})`,borderRadius:'50%',animation:'spin 0.75s linear infinite',margin:'0 auto 12px' }} />
        <p style={{ color:'#aaa',fontSize:14 }}>Cargando tienda...</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh',background:'#fafafa',fontFamily:"'DM Sans',system-ui,sans-serif" }}>
      {cashProduct && (
        <CashModal
          product={cashProduct}
          config={config}
          clientId={clientId}
          onClose={() => setCashProduct(null)}
        />
      )}

      {/* Mini-modal email para pago con tarjeta */}
      {cardPrompt && (
        <div
          style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1000,
            display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}
          onClick={() => setCardPrompt(null)}
        >
          <div
            style={{ background:'#fff',borderRadius:18,padding:28,width:'100%',maxWidth:380,
              boxShadow:'0 24px 64px rgba(0,0,0,0.22)',fontFamily:"'DM Sans',system-ui,sans-serif" }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ color:'#1a1a2e',fontSize:16,fontWeight:700,margin:'0 0 4px' }}>
              {cardPrompt.nombre}
            </h3>
            <p style={{ color:`rgb(${rgb})`,fontSize:20,fontWeight:800,margin:'0 0 18px' }}>
              {currencySymbol(config.moneda)}{Number(cardPrompt.precio).toLocaleString('es-AR')}
            </p>
            <label style={{ display:'block',fontSize:13,fontWeight:600,color:'#555',marginBottom:6 }}>
              Tu email (para recibir el comprobante)
            </label>
            <input
              type="email"
              value={cardEmail}
              autoFocus
              onChange={e => { setCardEmail(e.target.value); setCardEmailErr(''); }}
              onKeyDown={e => e.key === 'Enter' && handleCardConfirm()}
              placeholder="ejemplo@correo.com"
              style={{
                width:'100%',padding:'11px 14px',borderRadius:10,marginBottom:6,
                border: cardEmailErr ? '1.5px solid #ef4444' : `1.5px solid #e5e7eb`,
                fontSize:14,outline:'none',fontFamily:'inherit',boxSizing:'border-box',
              }}
              onFocus={e => { e.target.style.borderColor=`rgb(${rgb})`; }}
              onBlur={e => { e.target.style.borderColor= cardEmailErr ? '#ef4444' : '#e5e7eb'; }}
            />
            {cardEmailErr && (
              <p style={{ color:'#ef4444',fontSize:13,margin:'0 0 10px',fontWeight:500 }}>{cardEmailErr}</p>
            )}
            <button
              onClick={handleCardConfirm}
              style={{
                width:'100%',padding:'12px 0',borderRadius:10,border:'none',
                background:`rgb(${rgb})`,color:'#fff',fontWeight:700,fontSize:15,
                cursor:'pointer',fontFamily:'inherit',marginTop: cardEmailErr ? 0 : 10,
              }}
            >
              Ir a pagar con Mercado Pago
            </button>
          </div>
        </div>
      )}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Syne:wght@700;800&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>

      {/* ── Header ── */}
      <header style={{
        background:`linear-gradient(135deg,rgb(${rgb}) 0%,rgba(${rgb},0.75) 100%)`,
        padding:0,position:'relative',overflow:'hidden',
      }}>
        <div style={{ position:'absolute',inset:0,backgroundImage:`radial-gradient(circle at 75% 50%,rgba(255,255,255,0.14) 0%,transparent 55%)`,pointerEvents:'none' }} />
        <div style={{ maxWidth:1100,margin:'0 auto',padding:'36px 20px 32px',position:'relative' }}>
          <div style={{ display:'flex',alignItems:'center',gap:18,marginBottom:12 }}>
            <div style={{ width:58,height:58,background:'rgba(255,255,255,0.18)',backdropFilter:'blur(8px)',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',fontSize:26,flexShrink:0 }}>
              🏪
            </div>
            <div>
              <h1 style={{ color:'#fff',fontSize:28,fontFamily:"'Syne',sans-serif",fontWeight:800,lineHeight:1.1,margin:0 }}>
                {config.nombre || 'Tienda'}
              </h1>
              {config.descripcion && (
                <p style={{ color:'rgba(255,255,255,0.72)',fontSize:14,marginTop:5,margin:'5px 0 0' }}>
                  {config.descripcion}
                </p>
              )}
            </div>
          </div>
          <div style={{ color:'rgba(255,255,255,0.55)',fontSize:13 }}>
            {products.length} {products.length === 1 ? 'producto' : 'productos'} disponibles
          </div>
        </div>
      </header>

      {/* ── MP Status Banner ── */}
      {mpMsg && (
        <div style={{
          padding:'14px 20px',textAlign:'center',fontSize:14,fontWeight:500,
          background: mpMsg.startsWith('✅') ? '#f0fdf4' : mpMsg.startsWith('❌') ? '#fef2f2' : '#fffbeb',
          color:      mpMsg.startsWith('✅') ? '#166534' : mpMsg.startsWith('❌') ? '#991b1b' : '#92400e',
          borderBottom:'1px solid rgba(0,0,0,0.06)',
        }}>
          {mpMsg}
        </div>
      )}

      {/* ── Main content ── */}
      <div style={{ maxWidth:1100,margin:'0 auto',padding:'30px 20px' }}>

        {/* ── Search + Filters ── */}
        <div style={{ display:'flex',gap:10,marginBottom:28,flexWrap:'wrap',alignItems:'center' }}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Buscar productos..."
            style={{
              flex:1,minWidth:200,padding:'11px 16px',
              background:'#fff',border:'1px solid #e5e7eb',borderRadius:11,
              fontSize:14,outline:'none',fontFamily:'inherit',
              boxShadow:'0 1px 4px rgba(0,0,0,0.06)',transition:'all 0.2s',
            }}
            onFocus={e => { e.target.style.borderColor=`rgb(${rgb})`; e.target.style.boxShadow=`0 0 0 3px rgba(${rgb},0.1)`; }}
            onBlur={e => { e.target.style.borderColor='#e5e7eb'; e.target.style.boxShadow='0 1px 4px rgba(0,0,0,0.06)'; }}
          />

          {categories.length > 0 && (
            <div style={{ display:'flex',gap:7,flexWrap:'wrap' }}>
              {['', ...categories].map(cat => (
                <button
                  key={cat || '__all'}
                  onClick={() => setCategory(cat)}
                  style={{
                    padding:'9px 16px',borderRadius:9,
                    border:`1.5px solid ${category === cat ? `rgb(${rgb})` : '#e5e7eb'}`,
                    background: category === cat ? `rgba(${rgb},0.08)` : '#fff',
                    color: category === cat ? `rgb(${rgb})` : '#666',
                    fontWeight:600,fontSize:13,cursor:'pointer',fontFamily:'inherit',
                    transition:'all 0.14s',
                  }}
                >
                  {cat || 'Todos'}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Featured section ── */}
        {!search && !category && featured.length > 0 && (
          <div style={{ marginBottom:36 }}>
            <h2 style={{ color:'#1a1a2e',fontSize:18,fontWeight:700,fontFamily:"'Syne',sans-serif",marginBottom:16,display:'flex',alignItems:'center',gap:8 }}>
              ⭐ Destacados
            </h2>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:16 }}>
              {featured.map(p => (
                <ProductCard key={p.id} product={p} config={config} onBuyMP={handleBuyMP} mpBuying={mpBuying} onBuyCash={setCashProduct} />
              ))}
            </div>
          </div>
        )}

        {/* ── All products ── */}
        {(!search && !category && featured.length > 0) && (
          <h2 style={{ color:'#1a1a2e',fontSize:18,fontWeight:700,fontFamily:"'Syne',sans-serif",marginBottom:16 }}>
            📦 Todos los productos
          </h2>
        )}

        {filtered.length === 0 ? (
          <div style={{ textAlign:'center',padding:'56px 20px' }}>
            <div style={{ fontSize:52,marginBottom:12 }}>🔍</div>
            <p style={{ color:'#aaa',fontSize:15 }}>No encontramos productos que coincidan.</p>
          </div>
        ) : (
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:16 }}>
            {(search || category ? filtered : nonFeatured).map(p => (
              <ProductCard key={p.id} product={p} config={config} onBuyMP={handleBuyMP} mpBuying={mpBuying} onBuyCash={setCashProduct} />
            ))}
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <footer style={{ marginTop:52,borderTop:'1px solid #e5e7eb',padding:'20px',textAlign:'center' }}>
        <p style={{ color:'#bbb',fontSize:12 }}>
          {config.nombre || 'Tienda'} · Tienda online
          {config.whatsapp && (
            <>
              {' · '}
              <a href={`https://wa.me/${config.whatsapp}`} target="_blank" rel="noreferrer"
                style={{ color:'#25D366',textDecoration:'none',fontWeight:500 }}>
                Contactar por WhatsApp
              </a>
            </>
          )}
        </p>
      </footer>
    </div>
  );
}
